import { Component, Input, AfterViewInit, ViewChild, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { SharedModule } from '@Shared/shared.module';
import { MatTableDataSource } from '@angular/material/table';
import { TeamsService, CaseAssigneeService, NotificationService, NotificationSeverity } from '@Services';
import { CaseAssignee } from '@Models';
import { getSlaStatusFromDueDates } from '@Utils/sla-utils';
import { SubSink } from 'subsink';

export interface AssignedCaseQueueItem {
    queueName: string;
    dueDate?: string;
    useBusinessHours?: boolean;
    queueId?: string;
    slaHours?: number;
    isSlaEnabled?: boolean;
}

export interface AssignedCaseTableRow {
    account: string;
    accountId: string;
    parentAccountId: string;
    caseDisplayNumber: string;
    policyNumber: string;
    assignedTo: string;
    assignedToUserId: string;
    faceAmount: number;
    queue: AssignedCaseQueueItem[];
    requirementsReceived: string[];
    caseId: string;
    openTasks: { name: string }[];
}

@Component({
  selector: 'assigned-cases-table',
  styleUrl: './assigned-cases-table.component.scss',
  templateUrl: './assigned-cases-table.component.html',
  imports: [SharedModule]
})
export class AssignedCasesTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    private _sort: MatSort;
    private _paginator: MatPaginator;
    private subs = new SubSink();

    @ViewChild(MatSort)
    set sort(sort: MatSort) {
        this._sort = sort;
        this.assignTableFeatures();
    }
    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatPaginator)
    set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.assignTableFeatures();
    }
    get paginator(): MatPaginator {
        return this._paginator;
    }

    @Input() cases: AssignedCaseTableRow[] = [];
    @Input() clientId: string;
    @Input() teamId: string;
    @Input() teamUsers: { id: string; userName: string }[] = [];
    @Input() filterByAssignedToUserIds: string[] = [];
    @Input() userCaseCounts: Record<string, number> = {};
    @Input() activeFilterCount: number = 0;
    @Input() tasksEnabled: boolean = false;
    @Output() assignedToChanged = new EventEmitter<{ caseId: string, userId: string }>();
    @Output() casesAssigned = new EventEmitter<void>();
    displayedColumns: string[] = ['selection', 'account', 'caseDisplayNumber', 'policyNumber', 'assignedTo', 'faceAmount', 'queue', 'requirementsReceived'];
    dataSource = new MatTableDataSource<AssignedCaseTableRow>([]);
    selection: SelectionModel<AssignedCaseTableRow>;
    bulkAssignUserId: string | null = null;
    selectedAccountIds: string[] = [];
    assignableTeamUsers: { id: string; userName: string }[] = [];

    isSideNavHidden = false;

    constructor(
        private caseAssigneeService: CaseAssigneeService,
        private notificationService: NotificationService,
        private teamsService: TeamsService
    ) {
        this.selection = new SelectionModel<AssignedCaseTableRow>(true, []);
    }

    stopPropagation($event: Event): void {
        $event.stopPropagation();
    }

    ngOnInit(): void {
        this.isSideNavHidden = document.getElementById('sidenav')?.classList.contains('hidenav') ?? false;
        this.subs.sink = this.selection.changed.subscribe(() => {
            this.updateSelectedAccountIds();
            this.loadAssignableTeamUsers();
        });
    }

    ngAfterViewInit(): void {
        this.assignTableFeatures();

        const sidenav = document.getElementById('sidenav');
        if (!sidenav) return;

        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === 'class') {
                    this.isSideNavHidden = sidenav.classList.contains('hidenav');
                }
            });
        });

        observer.observe(sidenav, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngOnChanges(changes?: SimpleChanges): void {
        if (changes?.['teamId'] && !changes['teamId'].firstChange) {
            this.resetSort();
        }
        const baseColumns = ['selection', 'account', 'caseDisplayNumber', 'policyNumber', 'assignedTo', 'faceAmount', 'queue', 'requirementsReceived'];
        this.displayedColumns = this.tasksEnabled ? [...baseColumns, 'openTasks'] : baseColumns;
        const raw = this.cases || [];
        const filtered = this.filterByAssignedToUserIds?.length > 0
            ? raw.filter(row => this.filterByAssignedToUserIds.includes(row.assignedToUserId))
            : raw;
        const sorted = [...filtered].sort((a, b) => (a.caseId || '').localeCompare(b.caseId || ''));
        this.dataSource.data = sorted;
        this.assignTableFeatures();
    }

    private resetSort(): void {
        if (!this._sort) {
            return;
        }
        this._sort.active = '';
        this._sort.direction = '';
        this._sort.sortChange.emit({ active: '', direction: '' });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    updateSelectedAccountIds(): void {
        const ids = this.selection.selected
            .map(row => row.accountId)
            .filter((id): id is string => !!id);
        this.selectedAccountIds = [...new Set(ids)];
    }

    loadAssignableTeamUsers(): void {
        if (!this.clientId || !this.teamId || this.selectedAccountIds.length === 0 || !this.teamUsers?.length) {
            this.assignableTeamUsers = [];
            return;
        }
        this.teamsService.getTeamMembersByAccountAccess(this.clientId, this.teamId, this.selectedAccountIds, true).subscribe({
            next: (userIds: string[]) => {
                const idSet = new Set(Array.isArray(userIds) ? userIds : []);
                this.assignableTeamUsers = this.teamUsers.filter(u => idSet.has(u.id));
            },
            error: () => this.assignableTeamUsers = []
        });
    }

    assignTableFeatures(): void {
        if (this.dataSource && this._sort && this._paginator) {
            this.dataSource.sort = this._sort;
            this.dataSource.sortingDataAccessor = (item, property) => {
                switch (property) {
                    case 'account': return item.account.toLowerCase();
                    case 'caseDisplayNumber': return item.caseDisplayNumber.toLowerCase();
                    case 'policyNumber': return item.policyNumber.toLowerCase();
                    case 'assignedTo': return item.assignedTo.toLowerCase();
                    case 'faceAmount': return item.faceAmount;
                    default: return '';
                };
            };
            this.dataSource.paginator = this._paginator;
        }
    }

    onBulkAssignTo(userId: string): void {
        if (!userId || !this.clientId || this.selection.selected.length === 0) {
            return;
        }
        const caseAssignees: CaseAssignee[] = this.selection.selected.map(row => ({
            caseId: row.caseId,
            userId,
            clientCode: this.clientId
        }));
        this.caseAssigneeService.setCaseUserAssignee(caseAssignees).subscribe({
            next: (response: any) => {
                const completedCases: any[] = [];
                const errorCases: any[] = [];
                (Array.isArray(response) ? response : [response]).forEach((res: any) => {
                    if (typeof res.userErrors !== 'undefined') {
                        errorCases.push(res);
                    } else {
                        completedCases.push(res);
                    }
                });

                const user = this.assignableTeamUsers.find(u => u.id === userId);
                const name = user?.userName ?? userId;
                const toastMsgParts: string[] = [];

                if (completedCases.length > 0) {
                    toastMsgParts.push(
                        `${completedCases.length} case${completedCases.length > 1 ? 's have' : ' has'} been assigned to ${name}.`
                    );
                }

                if (errorCases.length > 0) {
                    const dupCount = errorCases.filter((c: any) =>
                        (Array.isArray(c.userErrors)
                            ? c.userErrors.includes('This user has already been assigned to this case.')
                            : c.userErrors === 'This user has already been assigned to this case.')
                    ).length;

                    if (dupCount > 0) {
                        toastMsgParts.push(
                            `${dupCount} case${dupCount > 1 ? 's' : ''} selected ${dupCount > 1 ? 'are' : 'is'} already assigned to that user.`
                        );
                    }
                    
                    const otherErrorsCount = errorCases.length - dupCount;
                    if (otherErrorsCount > 0) {
                        toastMsgParts.push(
                            `${otherErrorsCount} case${otherErrorsCount > 1 ? 's' : ''} selected ${otherErrorsCount > 1 ? 'are' : 'is'} errored.`
                        );
                    }
                }

                if (toastMsgParts.length > 0) {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Info,
                        message: toastMsgParts.join(' ')
                    });
                }
                this.bulkAssignUserId = null;
                this.selection.clear();
                this.casesAssigned.emit();
            },
            error: (err) => {
                this.notificationService.showNotification({
                    severity: NotificationSeverity.Error,
                    message: err?.message ?? 'Failed to assign cases.'
                });
            }
        });
    }

    getChipColor(dueDate: string): string {
        if (!dueDate) return 'gray';
        const statuses = getSlaStatusFromDueDates([dueDate]);
        if (statuses.includes('pastDue')) return 'red';
        if (statuses.includes('dueToday')) return 'orange';
        if (statuses.includes('dueTomorrow')) return 'green';
        return 'gray';
    }

    getRemainingHours(dueDate: string, useBusinessHours: boolean): number | null {
        if (!dueDate) return null;
        const statuses = getSlaStatusFromDueDates([dueDate]);
        if (statuses.includes('pastDue')) return null;
        
        const now = new Date();
        const due = new Date(dueDate);
        
        if (!useBusinessHours) {
            const diffMs = due.getTime() - now.getTime();
            const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
            return diffHours > 0 ? diffHours : null;
        }
        
        return this.calculateBusinessHours(now, due);
    }

    calculateBusinessHours(start: Date, end: Date): number | null {
        const BUSINESS_START_HOUR = 9;
        const BUSINESS_END_HOUR = 17;
        
        if (start >= end) return null;
        
        let totalBusinessHours = 0;
        let current = new Date(start);
        
        while (current < end) {
            const dayOfWeek = current.getDay();
            
            if (dayOfWeek === 0 || dayOfWeek === 6) {
                current.setDate(current.getDate() + 1);
                current.setHours(BUSINESS_START_HOUR, 0, 0, 0);
                continue;
            }
            
            const dayStart = new Date(current);
            dayStart.setHours(BUSINESS_START_HOUR, 0, 0, 0);
            
            const dayEnd = new Date(current);
            dayEnd.setHours(BUSINESS_END_HOUR, 0, 0, 0);
            
            const effectiveStart = new Date(Math.max(current.getTime(), dayStart.getTime()));
            const effectiveEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));
            
            if (effectiveStart < effectiveEnd) {
                const hoursThisDay = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60 * 60);
                totalBusinessHours += hoursThisDay;
            }
            
            current.setDate(current.getDate() + 1);
            current.setHours(BUSINESS_START_HOUR, 0, 0, 0);
        }
        
        return Math.ceil(totalBusinessHours);
    }

    isPastDue(dueDate: string): boolean {
        if (!dueDate) return false;
        const statuses = getSlaStatusFromDueDates([dueDate]);
        return statuses.includes('pastDue');
    }

    clearSelection(): void {
        this.selection.clear();
        this.bulkAssignUserId = null;
    }

    getOpenTasksTooltip(openTasks: { name: string }[]): string {
        return openTasks.map(t => t.name).join('\n');
    }
}